"use client"

import { useButton } from 'react-aria';
import React, { forwardRef, useEffect, useRef, useState } from 'react';

import { BeatLoader } from 'react-spinners';

import buttonStyles from './Button.module.css'

import useResizeObserver from 'bwax-ui/hooks/useResizeObserver';

import type { PressEvent } from '@react-types/shared';

import clsx from "clsx";

// import PopoverTrigger from 'bwax-ui/components/PopoverTrigger';
import DropdownMenu from 'bwax-ui/components/DropdownMenu';
import useForwardedRef from '../hooks/useForwardedRef';


interface ButtonProps {
    children?: React.ReactNode;
    appearance?: 'default' | 'primary' | 'outline' | 'borderless',
    color?: string,
    size?: 'mini' | 'small' | 'normal' | 'large' | 'xlarge',
    isLoading?: boolean,
    isDisabled?: boolean,
    style?: React.CSSProperties,
    className?: string,
    autoFocus?: boolean,
    onPress?: (e: PressEvent) => void
}
export type ButtonRef = HTMLButtonElement;

const Button = React.forwardRef<ButtonRef, ButtonProps>((props, forwardedRef) => {

    const ref = useRef<HTMLButtonElement | null>(null);

    const upperRef = useForwardedRef(forwardedRef);

    const {
        appearance = "default", color = "gray", size = "normal", isLoading, children, isDisabled, style = {}, className,
        autoFocus,
    } = props;

    const { buttonProps } = useButton({
        ...props,
        isDisabled: isLoading || isDisabled
    }, ref);

    const sizeRef = useRef<{ width: number, height: number } | undefined>(undefined);
    const [fontColor, setFontColor] = useState<string | undefined>(); // used for the spinner

    // 如果 isLoading， 把 children 换成 spinner，但保持按钮原来的大小
    useResizeObserver(ref, (entry) => {
        const { borderBoxSize } = entry;
        if (!isLoading && borderBoxSize && borderBoxSize[0]) {
            sizeRef.current = { width: borderBoxSize[0].inlineSize, height: borderBoxSize[0].blockSize };
        }
    });
    
    useEffect(() => {
        if (ref.current && typeof (window) !== 'undefined') {
            const fontColor = window.getComputedStyle(ref.current)["color"];
            setFontColor(fontColor)
        }
    }, [ref.current])


    const getStyle = () => {
        if (isLoading && sizeRef.current) {
            return { ...style, ...sizeRef.current, padding: 0 }
        } else {
            return style
        }
    }

    const renderLoading = () => {

        const height = sizeRef.current ? sizeRef.current.height * 0.2 : 6;
        return (
            <BeatLoader color={fontColor} size={height} />
        )
    }

    return (
        <button autoFocus={autoFocus} ref={r => {
            ref.current = r;
            if (upperRef) {
                (upperRef as any).current = r;
            }

        }} style={getStyle()} className={clsx(className, buttonStyles["lc-button"])}
            data-color={color} data-appearance={appearance} data-loading={isLoading} data-size={size}
            {...buttonProps}
        >
            {isLoading ? renderLoading() : children}
        </button>
    );
})


interface ButtonDivProps {
    children?: React.ReactNode;
    appearance?: 'default' | 'primary' | 'outline' | 'borderless',
    color?: 'gray' | 'mauve' | 'slate' | 'sage' | 'olive' | 'sand' | 'sky' | 'mint' | 'lime' | 'yellow' | 'amber' | 'orange' | 'red' | 'crimson' | 'pink' | 'plum' | 'purple' | 'violet' | 'indigo' | 'blue' | 'cyan' | 'teal' | 'green' | 'grass',
    size?: 'mini' | 'small' | 'normal' | 'large' | 'xlarge',
    // isLoading?: boolean,
    // isDisabled?: boolean,
    style?: React.CSSProperties,
    className?: string,
    // autoFocus?: boolean
}

type Ref = HTMLDivElement;

export const ButtonStyleDiv = React.forwardRef<Ref, ButtonDivProps>(({ className, style, children, size = "normal", appearance = "default", color = "gray" }, ref) => {
    return (
        <div ref={ref} style={style} className={clsx(buttonStyles["lc-button"], className)}
            data-color={color} data-appearance={appearance} data-size={size}
        >
            {children}
        </div>
    );
})

export function buttonDrodownMenu(items = [], options: any /* to define type */, buttonOptions: ButtonDivProps, children: React.ReactNode) {
    if (items.length == 0) {
        return null
    }

    const { className, style, size = "normal", appearance = "default", color = "gray" } = buttonOptions;

    return (
        <DropdownMenu items={items.filter(x => !!x)} {...options}>
            <div style={style} className={clsx(buttonStyles["lc-button"], className)}
                data-color={color} data-appearance={appearance} data-size={size}
            >
                {children}
            </div>
        </DropdownMenu>
    )
}




interface PressableProps {
    children: React.ReactElement;
    style?: React.CSSProperties,
    className?: string,
    tabIndex?: number,
    asChild?: boolean,
    // autoFocus?: boolean
    onPress?: (e: PressEvent) => void,
    isDisabled?: boolean,
}

// plain button
export const Pressable = React.forwardRef<Ref, PressableProps>(({ className, isDisabled = false, style, children, asChild = true, tabIndex, ...otherProps }, forwardedRef) => {

    const ref = useForwardedRef(forwardedRef);

    const { buttonProps } = useButton({
        ...otherProps,
    }, ref);

    if(isDisabled) {
        return children
    }


    if (asChild) {
        const isCustomElement = children && children.type && typeof (children.type) !== "string";
        const props = { 
            ...(isCustomElement ? otherProps : buttonProps), 
            ref, 
            tabIndex: tabIndex || "0" 
        };
        return React.cloneElement(children, props as any);
    } else {
        return (
            <div className={className} style={style} {...buttonProps} tabIndex={tabIndex || 0}>
                {children}
            </div>
        )
    }

});

interface IconButtonProps {
    children: React.ReactElement;
    style?: React.CSSProperties,
    appearance?: 'default' | 'primary' | 'outline' | 'borderless',
    color?: 'gray' | 'mauve' | 'slate' | 'sage' | 'olive' | 'sand' | 'sky' | 'mint' | 'lime' | 'yellow' | 'amber' | 'orange' | 'red' | 'crimson' | 'pink' | 'plum' | 'purple' | 'violet' | 'indigo' | 'blue' | 'cyan' | 'teal' | 'green' | 'grass',
    size?: 'mini' | 'small' | 'normal' | 'large' | 'xlarge',
    isLoading?: boolean,
    isDisabled?: boolean,
    className?: string,
    // autoFocus?: boolean
    onPress?: (e: PressEvent) => void,
}

export const IconButton = React.forwardRef<ButtonRef, IconButtonProps>(({ className, color = "gray", style, children, ...otherProps }, forwardedRef) => {

    const ref = useForwardedRef(forwardedRef);

    const { buttonProps } = useButton({
        ...otherProps,
    }, ref);

    return (
        <button ref={ref} className={clsx("lc-icon-button", className)} data-color={color} style={style} {...buttonProps}>
            {children}
        </button>
    )
});


export function iconButton(icon: React.ReactNode, onPress: (e: PressEvent) => void, { 
    size = "normal", key, unstyledFocus, isDisabled, color = "gray"
} : { size?: string, key?: string, unstyledFocus?: boolean, isDisabled?: boolean, color?: string } = {}) {
    return (
        <Pressable onPress={onPress} key={key} isDisabled={isDisabled}>
            <div data-color={color} className={clsx("rounded flex justify-center items-center", {
                "w-[2rem] h-[2rem] min-w-[2rem] text-[18px] ": size == "normal",
                "w-[1.75rem] h-[1.75rem]  min-w-[1.75rem] text-[16px]": size == "small",
                "unstyled-focus": unstyledFocus,
                "hover:bg-[var(--color-3)] text-[var(--color-11)]": !isDisabled,
                "opacity-50 cursor-not-allowed": isDisabled 
            })}>
                {icon}
            </div>
        </Pressable>

    )
}

export function ToggleIconButton({ icon, isActive = false, onChange = _ => {}, size = "normal" }: 
    { icon: React.ReactNode, isActive: boolean, onChange: (iActive: boolean) => void, size: string }
) {

    return (
        <Pressable onPress={_ => onChange(!isActive)}>
            <div className={clsx("rounded flex justify-center items-center ", {
                "w-[2rem] h-[2rem] min-w-[2rem] text-[18px] ": size == "normal",
                "w-[1.75rem] h-[1.75rem]  min-w-[1.75rem] text-[16px]": size == "small",
                "hover:bg-[--gray-3] text-[var(--gray-11)]": !isActive,
                "hover:bg-[--main-3] bg-[--main-2] text-[var(--main-11)]": isActive,
            })}>
                {icon}
            </div>
        </Pressable>

    )
}


export function iconDropdownMenu(icon: React.ReactNode, items: any[] = [], { size = "normal", key, unstyledFocus, isDisabled, ...dropdownOptions }: { size?: string, key?: string, unstyledFocus?: boolean, isDisabled?: boolean, [key: string]: any } = {}) {
    if (items.length == 0) {
        return null
    }

    return (
        <DropdownMenu items={items.filter(x => !!x)} {...dropdownOptions} key={key}>
            <div className={clsx(
                "rounded flex justify-center items-center",
                {
                    "w-[2rem] h-[2rem] min-w-[2rem] text-[18px] ": size == "normal",
                    "w-[1.75rem] h-[1.75rem]  min-w-[1.75rem] text-[16px]": size == "small",
                    "unstyled-focus": unstyledFocus,
                    "hover:bg-[var(--gray-3)] text-[var(--gray-11)]": !isDisabled,
                    "opacity-50 cursor-not-allowed": isDisabled
                }
            )}>
                {icon}
            </div>
        </DropdownMenu>
    )
}




export function create(props: ButtonProps) {
    return <Button {...props} />
}

export default Button;